import axios from 'axios'
import { authedJsonHeaders, firebaseConfig, storage } from './firebase'
import { getDownloadURL, ref } from 'firebase/storage'
import { getAuth, signOut } from 'firebase/auth'
import { fetchFreshToken, getStoredToken } from './services/tokenManager'

export const baseURL = window.appConfig.API_URL

export const BASE_URL = process.env.REACT_APP_LA_URL

// export const BASE_URL = 'https://mysimon-call-tales-division.trycloudflare.com'

/**
 * ===============
 *  HELPER UTILS
 * ===============
 */
function buf2hex(buffer) {
  return Array.prototype.map
    .call(new Uint8Array(buffer), (x) => ('00' + x.toString(16)).slice(-2))
    .join('')
}

function getDeviceUid() {
  if (localStorage.deviceUid) return localStorage.deviceUid

  const bytes = new Uint8Array(8)
  window.crypto.getRandomValues(bytes)
  localStorage.deviceUid = buf2hex(bytes)
  return localStorage.deviceUid
}

export class ApiError extends Error {
  constructor(error) {
    super(error?.message || 'ApiError')
    Object.assign(this, {}, error)
  }
}

/**
 * ==================================================
 * FIRESTORE API CLIENT  (your existing functionality)
 * ==================================================
 */
export function createApiClient(token) {
  const projectId = firebaseConfig.projectId
  const baseUrl = `https://firestore.googleapis.com/v1/projects/${projectId}/databases/${process.env.REACT_APP_ENV === 'production' ? '(default)' : 'live-alarm-database-stage'}/documents`
  // const baseUrl = `https://firestore.googleapis.com/v1/projects/${projectId}/databases/(default)/documents`
  return async function apiRequest(
    endpoint,
    payload,
    method = payload ? 'POST' : 'GET'
  ) {
    try {
      const url = `${baseUrl}/${endpoint}`
      const headers = {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      }

      const response = await fetch(url, {
        method,
        headers,
        body: payload ? JSON.stringify(payload) : undefined
      })

      if (!response.ok) {
        const errorData = await response.json()
        const errorMessage = errorData.error?.message || 'Request failed'

        // Handle auth issues
        if (
          errorMessage.includes('Missing or invalid authentication') ||
          errorMessage.includes('UNAUTHENTICATED')
        ) {
          const auth = getAuth()
          try {
            const freshToken = await auth.currentUser.getIdToken(true)

            const retryResponse = await fetch(url, {
              method,
              headers: { ...headers, Authorization: `Bearer ${freshToken}` },
              body: payload ? JSON.stringify(payload) : undefined
            })

            if (retryResponse.ok) return await retryResponse.json()
          } catch (refreshError) {
            console.error('Token refresh failed:', refreshError)
          }

          await signOut(auth)
        }

        throw new Error(errorMessage)
      }

      return await response.json()
    } catch (err) {
      console.error('API Error:', err)
      throw new Error(
        err.message || 'An error occurred while making the request'
      )
    }
  }
}

/**
 * ===========================================
 * NEW: GLOBAL REST API CLIENT (your requirement)
 * ===========================================
 * Usage:
 * const rest = createRestClient(token)
 * rest.get("/api/users")
 * rest.post("/api/login", payload)
 */
export function createRestClient() {
  const api = axios.create({
    baseURL: BASE_URL,
    timeout: 20000
  })

  // 🔥 Add token to every request
  api.interceptors.request.use((config) => {
    const token = getStoredToken()
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  })

  // 🔥 Handle 401 errors and retry
  api.interceptors.response.use(
    (response) => response,

    async (error) => {
      const originalRequest = error.config

      // Already retried once? Don't retry again
      if (originalRequest._retry) return Promise.reject(error)

      // Only retry on 401 unauthorized
      if (error.response?.status === 401) {
        originalRequest._retry = true

        try {
          // 🔥 Get fresh token from /verify API
          const newToken = await fetchFreshToken()

          // Set new token to header
          originalRequest.headers.Authorization = `Bearer ${newToken}`

          // Retry original request
          return api(originalRequest)
        } catch (e) {
          // Refresh failed → logout user
          console.error('Token refresh failed:', e)
          return Promise.reject(e)
        }
      }

      return Promise.reject(error)
    }
  )

  return {
    get: (url, params) => api.get(url, { params }).then((r) => r.data),
    post: (url, data) => api.post(url, data).then((r) => r.data),
    put: (url, data) => api.put(url, data).then((r) => r.data),
    patch: (url, data) => api.patch(url, data).then((r) => r.data),
    delete: (url) => api.delete(url).then((r) => r.data),
    raw: api
  }
}

/**
 * ===========================================
 * OTHER EXISTING HELPERS (keep same)
 * ===========================================
 */

export async function getResourceDataUrl(url, accessToken) {
  const res = await axios.get(url, {
    headers: {
      authorization: `Bearer ${accessToken}`,
      'X-Device-ID': getDeviceUid()
    },
    responseType: 'blob',
    timeout: 3.6e7
  })

  return URL.createObjectURL(res.data)
}

export async function openResource(url, accessToken) {
  const dataUrl = await getResourceDataUrl(url, accessToken)
  return window.open(dataUrl, '_blank')
}

export async function openResources(pathOrUrl, accessToken) {
  try {
    if (pathOrUrl.startsWith('https://')) {
      return window.open(pathOrUrl, '_blank')
    }

    const fileRef = ref(storage, pathOrUrl)
    const url = await getDownloadURL(fileRef)
    return window.open(url, '_blank')
  } catch (err) {
    alert('Unable to open the file.')
    console.error(err)
  }
}

/**
 * ===========================================
 * PIN ATTACHMENT SYNC API
 * ===========================================
 */
export async function syncPinAttachments(data) {
  const isProd = process.env.REACT_APP_ENV === 'production'
  const url = isProd
    ? 'https://us-central1-live-alarm-v2.cloudfunctions.net/syncPinAttachmentMessagesProd'
    : 'https://us-central1-live-alarm-v2.cloudfunctions.net/syncPinAttachmentMessagesStage'

  try {
    // The endpoint takes the sender from the ID token, so senderData is no
    // longer sent — a supplied one would only have been ignored.
    const headers = await authedJsonHeaders()
    if (!headers) {
      throw new Error('Not signed in')
    }
    const { senderData, ...body } = data // eslint-disable-line no-unused-vars

    const response = await axios.post(url, body, { headers })
    return response.data
  } catch (error) {
    console.error('Error syncing pin attachments:', error)
    throw error
  }
}

